import { useCallback, useEffect, useState } from "react";

import { useSearchParams } from "react-router-dom";

import { IBrand } from "../../models/Brand/Brand";
import Form from "../../components/base/Form/Form";
import WilmaLogo from "../../assets/logo.svg";
import useApiHelper from "../../hooks/useApiHelper";
import { Alert } from "../../components/base/Alert";
import { Button } from "../../components/base/Button";
import { useTheme } from "../../contexts/ThemeContext";
import { BrandRequest } from "../../models/auth/BrandRequest";
import LoadingWheel from "../../components/shared/LoadingWheel";
import { FormErrors } from "../../components/base/Form/FormErrors";
import { FormTextField } from "../../components/base/Form/FormTextField";
import { DefaultResponseWithData, DefaultResponse } from "../../models/system/DefaultResponse";
import {
  ResetPasswordRequest,
} from "../../models/auth/ResetRequest";
import { LoginResponse } from "../../models/auth/LoginResponse";
import { Separator } from "../../components/shared/Seperator";
import { url } from "inspector";
import { set } from "react-hook-form";

interface PasswordResetFormFields {
  username: string;
  password: string;
}

interface SetCookieRequest {
  name: string;
  value: string;
}

export default function PasswordReset() {
  const [token, setToken] = useState<string>();

  // add token as cookie
  const { post } = useApiHelper({
    apiUrl: undefined,
    headers: {
      "Authorization": `Bearer ${token}`
    },
    options: {
    }
  });

  // Can't send blank bearer as the auth service in API will be unhappy 
  const { post: postNoHeaders } = useApiHelper();

  const [urlParams] = useSearchParams();
  const [loading, setLoading] = useState(true);

  const [authError, setAuthError] = useState<string>();

  const [requesting, setRequesting] = useState(true);
  const [requested, setRequested] = useState(false);
  const [reset, setReset] = useState(false);

  // Older emails (and the invite email) only carry ?t=, so there's nowhere to send the user afterwards
  const canReturnToLogin = urlParams.has("returnUrl");

  //! The login page forwards returnUrl + ari/app here; pass them on so the emailed link
  //! brings the user back with the same app context, otherwise login has no app to enter.
  const handleRequestPasswordReset = async (data: PasswordResetFormFields) => {
    setLoading(true);

    try {
      const requestResponse = await postNoHeaders<
        ResetPasswordRequest,
        DefaultResponse
      >("/auth/password/request", {
        ...data,
        returnUrl: urlParams.get("returnUrl") ?? undefined,
        applicationRegistrationId: urlParams.get("ari") ?? undefined,
        applicationId: urlParams.get("app") ?? undefined,
      });


    } catch (e: any) {
      if (e?.status) {
        setAuthError(e.message);
      } else {
        setAuthError("Unexpected error, please contact support");
      }
    } finally {
      //! Timeout to stop flicker when redirecting
      setTimeout(() => setLoading(false), 200);
    }

    setRequested(true);
  };

  const handleResetPassword = async (data: PasswordResetFormFields) => {
    setLoading(true);

    try {
      // const setCookieResponse = await post<
      //     SetCookieRequest,
      //     DefaultResponse
      // >("/auth/cookie", { name: "token", value: token! });

      const resetResponse = await post<
        ResetPasswordRequest,
        DefaultResponse
      >("/auth/password/reset", { ...data });
      console.log(resetResponse);

      if (!resetResponse) {
        setAuthError(resetResponse);
        return;
      }

      // Don't bounce straight to returnUrl - they have no session yet. Show the success
      // message and let "Back to Login" take them to the login page with the app context.
    } catch (e: any) {
      if (e?.status) {
        setAuthError(e.message);
      } else {
        setAuthError("Unexpected error, please contact support");
      }
    } finally {
      //! Timeout to stop flicker when redirecting
      setTimeout(() => setLoading(false), 200);
    }

    setReset(true);
  };

  useEffect(() => {
    if (urlParams.has("t")) {
      const t = urlParams.get("t")!;
      setToken(t);
      setRequesting(false);
    }
    setLoading(false);
  })

  return (
    <div className="flex min-h-full flex-1 flex-col justify-center py-12 sm:pt-16 sm:px-6 lg:px-8">
      <div className="mb-12 flex justify-center sm:mb-16 sm:mx-auto sm:w-full sm:max-w-md">
        <img className="h-14" src={WilmaLogo} alt="Logo" />
      </div>
      <div className="sm:mx-auto sm:w-full sm:max-w-[480px]">
        <div className="bg-white px-6 py-12 shadow prose sm:rounded-lg sm:px-12">
          <h2>
            Reset Password
          </h2>
          {requested || reset ? (
            <>
              {requested && (
                <Alert
                  severity="success"
                  title="Password Reset Requested"
                  message="Please check your email for further instructions"
                />
              )}
              {reset && (
                <Alert
                  severity="success"
                  title="Password Reset Successful"
                  message={
                    canReturnToLogin
                      ? "Please go back to the login page to login with your new password"
                      : "Please go back to Wilma and login from there with your new password"
                  }
                />
              )}
              {/* With no returnUrl the login page has no application to log into, so don't send them there */}
              {(requested || canReturnToLogin) && (
                <Button className="mt-10" fullWidth onClick={(e) => {
                  e.preventDefault();
                  if (canReturnToLogin) {
                    let query = "returnUrl=" + urlParams.get("returnUrl");
                    if (urlParams.has("ari")) query += "&ari=" + urlParams.get("ari");
                    else if (urlParams.has("app")) query += "&app=" + urlParams.get("app");

                    window.location.assign("/?" + query);
                  } else {
                    window.location.assign("/");
                  }
                }}>
                  Back to Login
                </Button>
              )}
            </>
          ) : (
            <Form<PasswordResetFormFields>
              onSubmit={
                requesting
                  ? handleRequestPasswordReset
                  : handleResetPassword
              }
              className="space-y-4"
            >
              {({ register, watch }) =>
                loading ? (
                  <LoadingWheel />
                ) : (
                  <>
                    {authError && (
                      <Alert
                        severity="error"
                        title="Login Error"
                        message={authError}
                      />
                    )}
                    <FormErrors />

                    {requesting && (
                      <>
                        <FormTextField
                          label="Username"
                          {...register("username", { required: true })}
                        />
                        <Button
                          type="submit"
                          className="!mt-10"
                          fullWidth
                        >
                          Send
                        </Button>
                      </>
                    )}
                    {!requesting && (
                      <>
                        <FormTextField
                          label="Password"
                          type="password"
                          {...register("password", { required: true })}
                        />
                        <Button type="submit" className="!mt-10" fullWidth>
                          Reset
                        </Button>
                      </>
                    )}
                  </>
                )
              }
            </Form>
          )}

        </div>
      </div>
    </div>
  );
}